import {PathConfigMap} from '@react-navigation/core';

import {isDepartmentJoinId} from '../components/SiteHeader/menu/departmentJoins';
import {
  AirtableBrand,
  AirtableFeedItem,
  AirtableFeedItemStub,
  AirtablePromotionItem,
} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import {Department} from '../models/middleware';
import {AccountScreenAction, InformationContentType} from '../models/navigationTypes';
// Deliberately NOT the `queries/promotions` barrel: the barrel re-exports the
// `usePromotions` hook, and this module is not React.
import {readPromotions} from '../queries/promotions/promotionsAccess';
import {rootStore} from '../stores/rootStore';
import {OrderLine} from '../transport/apiServiceResponse';
import {codeifyName} from '../utils/string';

import {viewAllSubDepartment} from './viewAllSubDepartment';

/*
iOS:
- xcrun simctl openurl booted https://beta.sipnsave.com.au/brands/bundaberg
- xcrun simctl openurl booted https://beta.sipnsave.com.au/promotions/footy-wins-back
- xcrun simctl openurl booted https://beta.sipnsave.com.au/promotions
iOS/Android:
- npx uri-scheme open "https://bottlemart-brand-dev.lmg-bw.au/brands/bundaberg" --ios
- npx uri-scheme open "https://bottlemart-brand-dev.lmg-bw.au/brands/bundaberg" --android
*/

const stringifyDepartment = (department: Department) => codeifyName(department.name);

export const findDepartment = (departmentCode: string): Department | undefined => {
  const rangeStore = rootStore?.rangeStore;
  const department = rangeStore?.departments?.find(
    (rangeStoreDepartment: Department) =>
      codeifyName(rangeStoreDepartment.name) === departmentCode?.toLowerCase(),
  );
  return department;
};

// Sub-department names repeat across parents (Spirits > Gin, Premixed Drinks > Gin).
// A URL param parser sees one param only, so scoping to the parent happens here,
// called from SubDepartmentScreen.
export const findSubDepartment = (parentId: string, subCode: string): Department | undefined =>
  rootStore?.rangeStore?.departments?.find(
    (rangeStoreDepartment: Department) =>
      rangeStoreDepartment.parent?.id === parentId &&
      codeifyName(rangeStoreDepartment.name) === subCode?.toLowerCase(),
  );

const parseDepartmentCode = (departmentCode: string) => findDepartment(departmentCode);

const parseSubDepartmentCode = (departmentCode: string): Department | undefined => {
  if (departmentCode === 'view-all') {
    return viewAllSubDepartment();
  }
  return findDepartment(departmentCode);
};

type RNLinkingOptionsConfig = {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  initialRouteName?: keyof {};
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  screens: PathConfigMap<{}>;
};

export const navStructure: RNLinkingOptionsConfig = {
  screens: {
    // TODO: initialRouteNames!
    [AppRoute.ROOT]: {
      screens: {
        [AppRoute.MAIN]: {
          screens: {
            // Main/base/top routes
            [AppRoute.HOME]: '',
            [AppRoute.SHOP]: 'shop',
            [AppRoute.DISCOVER]: 'discover',
            [AppRoute.CATALOGUE]: 'catalogues',
            [AppRoute.CART]: 'cart',
            [AppRoute.REWARDS]: 'rewards',
            [AppRoute.MEMBERS_PRICE_LIST]: {
              path: 'shop/members/:external_sell_type',
            },
            [AppRoute.EXCLUSIVE_OFFER_LIST]: {
              path: 'offers',
            },

            // Drawer
            [AppRoute.MY_STORE]: 'my-store',
            [AppRoute.CONTACT_US]: 'contact-us',
            [AppRoute.INFORMATION]: 'information',
            [AppRoute.RESPONSIBLE_SERVICE]: 'responsible-service',
            [AppRoute.SELF_EXCLUSION]: 'self-exclusion',
            [AppRoute.WISHLIST]: 'wishlist', // not working
            [AppRoute.PROMOTIONS]: 'promotions', // not working

            [AppRoute.NEW_SEARCH]: 'search',

            // "Pages"
            // "Other"
            [AppRoute.ACCOUNT_PASSWORD_RESET]: 'password',

            // "Discover"
            [AppRoute.ARTICLE]: {
              path: 'discover/:article',
              stringify: {
                article: (article: AirtableFeedItem) => article.url || '',
              },
              parse: {
                article: (articleId: string): AirtableFeedItem | AirtableFeedItemStub => {
                  // React Navigation v7: Path params are no longer URL-encoded
                  // Decode defensively to support both old (encoded) and new (non-encoded) deep links
                  const decoded = decodeURIComponent(articleId);
                  const found = rootStore?.feedStore?.discoverList?.find(
                    (feedItem: AirtableFeedItem) =>
                      feedItem.url?.toLowerCase() === decoded?.toLowerCase() ||
                      feedItem.url?.toLowerCase() === articleId?.toLowerCase(),
                  );
                  if (found) return found;
                  // DIS-1201: discoverList not loaded yet (cold-start deep link).
                  // FeedDiscoverDetailScreen narrows on '__pending' in article and
                  // re-derives the real article once discoverList populates.
                  return {url: decoded, __pending: true};
                },
              },
            },

            // "Shop"
            [AppRoute.PRODUCT]: {
              path: 'shop/product/:product',
              stringify: {
                product: (product: OrderLine) => product?.product_id,
              },
              parse: {
                product: (productId: string) => productId,
              },
            },
            [AppRoute.FAVOURITE_BRANDS]: 'shop/brands',
            [AppRoute.BRAND]: {
              path: 'shop/brands/:brand',
              stringify: {
                brand: (brand: AirtableBrand) =>
                  brand.brand ? brand.brand.replaceAll(' ', '-') : '',
              },
              parse: {
                brand: (brandName: string) => {
                  // React Navigation v7: Path params are no longer URL-encoded
                  // Decode defensively to support both old (encoded) and new (non-encoded) deep links
                  const decoded = decodeURIComponent(brandName);
                  return rootStore?.brandsStore?.brands?.find(
                    (brand: AirtableBrand) =>
                      brand.brand?.toLowerCase() === decoded?.toLowerCase() ||
                      brand.brand?.toLowerCase() === brandName?.toLowerCase(),
                  );
                },
              },
            },
            [AppRoute.DEPARTMENT]: {
              path: 'shop/categories/:department',
              stringify: {
                department: stringifyDepartment,
              },
              parse: {
                department: parseDepartmentCode,
                specials: (v: string) => v === 'true',
              },
            },
            [AppRoute.SHOP_ALL]: {
              path: 'shop/all/:join',
              parse: {
                join: (v: string) => (isDepartmentJoinId(v) ? v : undefined),
                specials: (v: string) => v === 'true',
              },
            },
            [AppRoute.SUB_DEPARTMENT]: {
              path: 'shop/categories/:department/:subDepartment',
              stringify: {
                department: stringifyDepartment,
                subDepartment: stringifyDepartment,
              },
              parse: {
                department: parseDepartmentCode,
                subDepartment: parseSubDepartmentCode,
              },
            },

            [AppRoute.EXCLUSIVE_OFFER_DETAIL]: {
              path: 'offers/:offerId',
            },

            [AppRoute.ACCOUNT_EMAIL_VERIFICATION]: 'account/email-verification',

            // "Promotions"
            [AppRoute.PROMOTION]: {
              path: 'promotions/:promotion',
              stringify: {
                promotion: (promotion: AirtablePromotionItem) => promotion?.url || '',
              },
              parse: {
                promotion: (promotionId: string) => {
                  // DIS-1567: cache-only read. React Navigation does NOT await
                  // `parse`, so this cannot fetch. A cache miss yields [] and
                  // takes exactly the same path an empty rawPromotions took
                  // before: no `promotion` param, screen rehydrates itself.
                  // The `rootStore &&` is the old expression's `rootStore?.`
                  // kept intact: `readPromotions` dereferences
                  // `rootStore.shopSelectionStore`, and a throw inside `parse`
                  // propagates out of `getStateFromPath`, which `App.tsx:79`
                  // calls during render with no try/catch around it.
                  const rawPromotions =
                    (rootStore ? readPromotions(rootStore) : undefined)?.rawPromotions ?? [];
                  if (rawPromotions.length) {
                    // React Navigation v7: Path params are no longer URL-encoded
                    // Decode defensively to support both old (encoded) and new (non-encoded) deep links
                    const decoded = decodeURIComponent(promotionId);
                    const promotion = rawPromotions.find(
                      p =>
                        p.activationUrlName?.toLowerCase() === decoded.toLowerCase() ||
                        p.activationUrlName?.toLowerCase() === promotionId.toLowerCase(),
                    );
                    return promotion;
                  }
                },
              },
            },
            // TODO: Should 'promotions' link to "current promotions"?
            [AppRoute.PROMOTIONS_CURRENT]: 'promotions/current',
            [AppRoute.PROMOTIONS_WINNERS]: 'promotions/winners',
            // TODO: XXX This shows to iframe link in the URL ☠️☠️☠️☠️ DANGER
            [AppRoute.PROMOTION_IFRAME]: 'promotions/go',

            // Information
            [AppRoute.INFORMATION_CONTENT]: {
              initialRouteName: AppRoute.INFORMATION_CONTENT,
              path: 'information/:target',
              stringify: {
                target: (target: InformationContentType) => target?.toString(),
              },
              parse: {
                target: (target: string) => target as InformationContentType,
              },
            },

            [AppRoute.ACCOUNT_DELETE_INFO]: 'information/delete-account',

            // Contact Us
            [AppRoute.CONTACT_US_SUBMIT_TICKET]: 'contact-us/ticket',

            // Responsible Service
            [AppRoute.RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS]:
              'responsible-service/liquor-licensing',
            [AppRoute.RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST]:
              'responsible-service/third-party-support',

            // Account
            // TODO: this is reachable even if the user is already logged-in
            [AppRoute.ACCOUNT_NOT_LOGGED_IN]: 'check-email',
            [AppRoute.ACCOUNT_LOGIN]: 'login',
            [AppRoute.ACCOUNT_LOGGED_IN]: 'account',
            [AppRoute.ACCOUNT_RECENT_ORDERS]: 'account/orders',
            [AppRoute.ACCOUNT_ORDER_VIEW]: 'account/order/:orderNumber',
            [AppRoute.ACCOUNT_DELETE]: 'account/delete',
            [AppRoute.ACCOUNT_PAYMENT_METHOD]: 'account/payment',
            [AppRoute.ACCOUNT_CHANGE_PASSWORD]: {
              path: 'account/change-password',
              parse: {
                action: () => AccountScreenAction.CHANGE_PASSWORD,
              },
            },
            [AppRoute.ACCOUNT_CREATE]: {
              path: 'account/create',
              parse: {
                action: () => AccountScreenAction.CREATE,
              },
            },

            [AppRoute.ACCOUNT_DETAIL]: {
              path: 'account/update',
              parse: {
                action: () => AccountScreenAction.UPDATE,
              },
            },

            [AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS]: {
              path: 'account/missing-details',
              parse: {
                action: () => AccountScreenAction.COLLECT_MISSING_DETAILS,
              },
            },

            /**
             * Routes added since loyalty project
             */
            [AppRoute.ACCOUNT_PHONE_VERIFICATION]: 'account/phone-verification',
            [AppRoute.ACCOUNT_SUCCESS]: 'account/success',

            /**
             * This is the screen in account management. When hasRewards=false is similar to the more rewards tab.
             */
            [AppRoute.ACCOUNT_REWARDS]: 'account/rewards',

            [AppRoute.ACCOUNT_COMMUNICATIONS_PREFERENCES]: 'account/communications-preferences',

            [AppRoute.CART_ORDER_PROCESSING]: 'cart/processing',
            [AppRoute.CART_ORDER_COMPETITIONS]: 'cart/competitions',
            [AppRoute.CART_ORDER_CONFIRMATION]: 'cart/processed',
            [AppRoute.THREEDS_IFRAME_FULLSCREEN]: 'cart/3ds',

            // Select store
            [AppRoute.MY_STORE_SUBURB_SELECTION]: 'find-suburb',
            // MY_STORE_STORE_SELECTION intentionally excluded — URL must not
            // persist through splash cycle or it routes back here in a loop
            // [AppRoute.MY_STORE_STORE_SELECTION]: 'choose-store',

            // Gift Cards
            [AppRoute.GIFT_CARDS_PROMOTION_WAITER]: 'gift-card-loading',
            [AppRoute.GIFT_CARDS_OPTIONS]: 'gift-cards-menu',
            [AppRoute.GIFT_CARDS_PARTICIPATING_STORES]: 'gift-cards-participating-stores',
            [AppRoute.GIFT_CARDS_HOW_TO_REDEEM]: 'gift-cards-how-to-redeem',
            [AppRoute.GIFT_CARDS_CHECK_BALANCE]: 'gift-cards-balance',
            [AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS]: 'gift-cards-terms-and-conditions',

            // Special deep links
            [AppRoute.STORE_SWITCH_LINK]: 'store/:shopSalesforceId',

            // Gift Cards Store
            [AppRoute.GIFT_CARDS_MAIN]: {
              path: 'gift-cards',
              screens: {
                [AppRoute.GIFT_CARDS_HOME]: '/',
                [AppRoute.GIFT_CARDS_STORE_REVIEW]: 'review',
                [AppRoute.GIFT_CARDS_STORE_CHECKOUT]: 'checkout',
                [AppRoute.GIFT_CARDS_STORE_SECRET_CART]: 'secret-cart',
                [AppRoute.GIFT_CARDS_STORE_ORDER_CONFIRMATION]: 'order/confirmation',
              },
            },
          },
        },
      },
    },
    [AppRoute.ROOT_STORE_SELECTION_FLOW]: {
      screens: {
        [AppRoute.MY_STORE_SUBURB_SELECTION]: 'welcome/find-suburb',
        [AppRoute.MY_STORE_STORE_SELECTION]: 'welcome/choose-store',
        // These screens are here because we needed a quick solution to show
        // the Gift Card Promotion Details screen without the need to select a store.
        [AppRoute.ROOT_GIFT_CARDS_PROMOTION]: 'gift-card-promotion',
        [AppRoute.ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS]: 'gift-card-terms-and-conditions',
      },
    },
    /**
     * Checkout flow is separated now
     */
    [AppRoute.ROOT_CHECKOUT_FLOW]: {
      screens: {
        // Cart
        [AppRoute.CART_ORDER_CHECKOUT]: 'cart/checkout',
        [AppRoute.CART_ORDER_CHECKOUT_REVIEW]: 'cart/checkout-review',
        [AppRoute.CART_ORDER_CHECKOUT_PAYMENT]: 'cart/checkout-payment',
      },
    },
    [AppRoute.ROOT_REWARDS_FLOW]: {
      screens: {
        [AppRoute.REWARDS_SIGN_IN]: {
          path: 'rewards/sign-in',
          // Trim surrounding whitespace only; preserve original case so the
          // value matches the signUpOffers entry exactly as authored.
          parse: {
            signUpCode: (raw: string) => {
              try {
                return decodeURIComponent(raw).trim();
              } catch {
                return raw.trim();
              }
            },
            staff: (raw: string) => raw,
            // R2/C1: lmgCode is an opaque bearer secret — decode but never trim,
            // unlike signUpCode, so the value reaches the store byte-identical.
            lmgCode: (raw: string) => {
              try {
                return decodeURIComponent(raw);
              } catch {
                return raw;
              }
            },
          },
        },
        [AppRoute.REWARDS_STAFF_STORE_SELECTION]: 'rewards/staff-store-selection',
        [AppRoute.REWARDS_SIGN_UP]: 'rewards/sign-up',
        [AppRoute.REWARDS_VERIFICATION]: 'rewards/verification',
        [AppRoute.REWARDS_SUCCESS]: 'rewards/success',
        [AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED]: 'rewards/staff-activation-blocked',
        [AppRoute.REWARDS_ACCOUNT_NOT_FOUND]: 'rewards/account-not-found',
        [AppRoute.REWARDS_TERMS_AND_CONDITIONS]: 'rewards/terms-and-conditions',
        /**
         * When user clicks email link to complete full sign up
         */
        [AppRoute.REWARDS_FULL_SIGN_UP]: 'rewards/full-sign-up',

        /**
         * Routes for when User has clicked Join More Rewards
         */
        [AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS]: 'rewards/activate-terms-and-conditions',
        [AppRoute.REWARDS_ACTIVATE_VERIFICATION]: 'rewards/activate-verification',
        [AppRoute.REWARDS_ACTIVATE_SUCCESS]: 'rewards/activate-success',
        [AppRoute.REWARDS_REDIRECT]: 'rewards/redirect',
      },
    },
    [AppRoute.ROOT_PROMOTIONS_FLOW]: {
      screens: {
        [AppRoute.PROMOTIONS_OPT_OUT]: {
          path: 'promotions/opt-out',
          parse: {
            competitionCode: (competitionCode: string) => competitionCode,
            competitionName: (competitionName: string) => competitionName,
          },
        },
      },
    },
    [AppRoute.ROOT_APP_BUILD_INFO]: 'build-info',
  },
};
